<template>
  <div class="project-container">
    <!-- Hero Section -->
    <header class="hero-section">
      <div class="hero-content">
        <div class="hero-badge">Stage Professionnel</div>
        <h1 class="hero-title">GAGNE</h1>
        <p class="hero-subtitle">Application métier de gestion d'atelier peinture</p>
        <div class="hero-meta">
          <span class="meta-item"><i class="mdi mdi-domain"></i> Groupe BRIAND</span>
          <span class="meta-item"><i class="mdi mdi-account-hard-hat"></i> 220 collaborateurs</span>
          <span class="meta-item"><i class="mdi mdi-briefcase"></i> Première expérience pro</span>
        </div>
      </div>
    </header>

    <!-- Overview Card -->
    <section class="card">
      <div class="card-header">
        <i class="mdi mdi-information-outline"></i>
        <h2>Résumé du Projet</h2>
      </div>
      <div class="card-content">
        <p class="lead-text">
          <strong>GAGNE</strong> est une application web métier complète développée pour optimiser
          la gestion des stocks et la planification des opérations dans un atelier de peinture industrielle.
          Ce projet représente ma <strong>première expérience professionnelle</strong> en tant que développeur,
          m'ayant permis de participer à un <strong>cycle complet de développement</strong> en environnement
          industriel réel, de la conception initiale jusqu'au déploiement et à la formation des utilisateurs.
        </p>
        <p>
          Durant ce stage, j'ai travaillé au sein du <strong>Groupe BRIAND</strong>, une entreprise de
          220 collaborateurs spécialisée dans la construction métallique. L'objectif principal était de
          moderniser la gestion de l'atelier peinture en remplaçant les processus manuels et les fichiers
          Excel par une solution digitale robuste, adaptée aux contraintes d'un environnement industriel.
          Le projet devait répondre aux besoins des opérateurs sur le terrain tout en fournissant aux
          responsables les outils de suivi et de planification nécessaires à une gestion efficace.
        </p>

        <div class="tech-stack">
          <span class="tech-badge">Vue.js</span>
          <span class="tech-badge">Vuetify</span>
          <span class="tech-badge">Symfony</span>
          <span class="tech-badge">API Platform</span>
          <span class="tech-badge">MariaDB</span>
          <span class="tech-badge">Raspberry Pi</span>
        </div>
      </div>
    </section>

    <!-- Context Card -->
    <section class="card">
      <div class="card-header">
        <i class="mdi mdi-factory"></i>
        <h2>Contexte de l'Entreprise</h2>
      </div>
      <div class="card-content">
        <div class="company-grid">
          <div class="company-item">
            <div class="company-icon">
              <i class="mdi mdi-office-building"></i>
            </div>
            <div class="company-content">
              <h4>GAGNE - Construction Métallique</h4>
              <p>Filiale du groupe BRIAND, spécialisée dans la construction métallique avec 2 usines</p>
            </div>
          </div>

          <div class="company-item">
            <div class="company-icon">
              <i class="mdi mdi-target"></i>
            </div>
            <div class="company-content">
              <h4>Objectif du Stage</h4>
              <p>Moderniser la gestion de l'atelier peinture avec une interface adaptée aux opérateurs</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Architecture Card -->
    <section class="card">
      <div class="card-header">
        <i class="mdi mdi-sitemap"></i>
        <h2>Architecture & Technologies</h2>
      </div>
      <div class="card-content">
        <h3 class="section-subtitle">Architecture Micro-services</h3>
        <p>
          L'architecture adoptée repose sur une séparation stricte en <strong>micro-services</strong>, avec un
          découplage complet entre le front-end, le back-end et la base de données. Cette approche facilite
          grandement la maintenance en permettant de faire évoluer chaque composant indépendamment, tout en
          garantissant une évolutivité maximale pour répondre aux besoins futurs de l'entreprise.
        </p>
        <p>
          Le front-end développé en <strong>Vue.js</strong> avec <strong>Vuetify</strong> offre une interface
          moderne, responsive et adaptée à une utilisation tactile en atelier. Le back-end construit avec
          <strong>Symfony</strong> et <strong>API Platform</strong> expose une API REST documentée et
          performante qui gère toute la logique métier et communique avec une base de données
          <strong>MariaDB</strong> stockant l'ensemble des entités (Affaires, Ordres de Fabrication,
          Stocks, etc.).
        </p>

        <div class="feature-grid">
          <div class="feature-item">
            <div class="feature-icon">
              <i class="mdi mdi-vuejs"></i>
            </div>
            <h4>Vue.js + Vuetify</h4>
            <p>Interface utilisateur moderne, réactive et optimisée pour une utilisation tactile en atelier.
            Vuetify fournit des composants Material Design prêts à l'emploi, permettant de créer rapidement
            une interface professionnelle et cohérente, parfaitement adaptée aux écrans tactiles utilisés
            par les opérateurs sur le terrain.</p>
          </div>

          <div class="feature-item">
            <div class="feature-icon">
              <i class="mdi mdi-api"></i>
            </div>
            <h4>Symfony + API Platform</h4>
            <p>Back-end robuste exposant une API REST complète, documentée automatiquement et respectant
            les standards de l'industrie. API Platform simplifie la création des endpoints tout en offrant
            des fonctionnalités avancées comme la pagination, le filtrage et la validation automatique des
            données, garantissant une API performante et sécurisée.</p>
          </div>

          <div class="feature-item">
            <div class="feature-icon">
              <i class="mdi mdi-database"></i>
            </div>
            <h4>MariaDB</h4>
            <p>Base de données relationnelle assurant le stockage fiable et performant de toutes les entités
            métier du système : Affaires, Systèmes, Commandes, Ordres de Fabrication, Stocks, et leurs relations
            complexes. Le schéma a été conçu pour garantir l'intégrité des données tout en permettant des
            requêtes rapides même avec de gros volumes.</p>
          </div>

        </div>
      </div>
    </section>

    <!-- Features Card -->
    <section class="card">
      <div class="card-header">
        <i class="mdi mdi-star-four-points"></i>
        <h2>Fonctionnalités Clés</h2>
      </div>
      <div class="card-content">
        <p>
          L'application propose un ensemble complet de fonctionnalités couvrant tous les besoins de l'atelier
          peinture, depuis la création des affaires jusqu'au suivi de la facturation. Chaque fonctionnalité a
          été conçue en collaboration étroite avec les utilisateurs finaux pour garantir une adoption réussie
          et une réelle amélioration des processus existants.
        </p>

        <div class="features-list">
          <div class="feature-row">
            <div class="feature-row-icon">
              <i class="mdi mdi-folder-multiple"></i>
            </div>
            <div class="feature-row-content">
              <h4>Gestion Complète des Entités</h4>
              <p>Système complet de création, modification et consultation de toutes les entités métier :
              Affaires, Systèmes, Commandes et Ordres de Fabrication. Chaque entité dispose de son propre
              formulaire de saisie validé, de vues détaillées et de tableaux de bord permettant un suivi
              précis de l'ensemble des opérations de l'atelier.</p>
            </div>
          </div>

          <div class="feature-row">
            <div class="feature-row-icon">
              <i class="mdi mdi-calendar-clock"></i>
            </div>
            <div class="feature-row-content">
              <h4>Planification Visuelle</h4>
              <p>Interface de planification interactive avec fonctionnalité drag & drop permettant aux
              responsables d'organiser visuellement les ordres de fabrication dans le temps. Le système
              prend en compte les contraintes de capacité de l'atelier et alerte en cas de surcharge,
              facilitant une planification optimale des ressources.</p>
            </div>
          </div>

          <div class="feature-row">
            <div class="feature-row-icon">
              <i class="mdi mdi-hand-pointing-up"></i>
            </div>
            <div class="feature-row-content">
              <h4>Vue Opérateur Tactile</h4>
              <p>Interface spécifiquement optimisée pour une utilisation en atelier sur écrans tactiles.
              Les boutons sont suffisamment grands pour être utilisés avec des gants, les informations
              essentielles sont mises en avant, et le système permet le scan rapide des codes-barres
              pour identifier les stocks et pièces sans saisie manuelle.</p>
            </div>
          </div>

          <div class="feature-row">
            <div class="feature-row-icon">
              <i class="mdi mdi-package-variant"></i>
            </div>
            <div class="feature-row-content">
              <h4>Gestion du Stock</h4>
              <p>Module complet de gestion des stocks avec entrées et sorties tracées via scannette.
              Chaque mouvement est enregistré avec son timestamp, l'opérateur responsable et l'ordre de
              fabrication associé, garantissant une traçabilité complète. Le système affiche en temps réel
              les niveaux de stock et génère des alertes en cas de rupture.</p>
            </div>
          </div>

          <div class="feature-row">
            <div class="feature-row-icon">
              <i class="mdi mdi-microsoft-excel"></i>
            </div>
            <div class="feature-row-content">
              <h4>Connexion Excel</h4>
              <p>Intégration Power Query permettant aux services administratifs de se connecter directement
              à la base de données depuis Excel pour générer des rapports personnalisés, suivre l'avancement
              en temps réel des affaires et préparer la facturation. Cette fonctionnalité a permis de
              conserver les workflows Excel existants tout en bénéficiant de données toujours à jour.</p>
            </div>
          </div>

        </div>
      </div>
    </section>

    <!-- Hardware Card -->
    <section class="card hardware-card">
      <div class="card-header">
        <i class="mdi mdi-raspberry-pi"></i>
        <h2>Matériel Industriel</h2>
      </div>
      <div class="card-content">
        <p>
          Le projet impliquait l'intégration de divers équipements industriels pour créer un écosystème
          complet et fonctionnel en atelier. Cette dimension matérielle du projet m'a permis de découvrir
          les contraintes et spécificités du développement pour environnements industriels, où la robustesse
          et la fiabilité sont primordiales.
        </p>

        <div class="hardware-grid">
          <div class="hardware-item">
            <i class="mdi mdi-barcode-scan"></i>
            <h4>Scannettes</h4>
            <p>Intégration de scannettes industrielles pour la lecture rapide et fiable des codes-barres.
            Ces dispositifs permettent aux opérateurs d'identifier instantanément les pièces et stocks sans
            saisie manuelle, réduisant drastiquement les erreurs et accélérant les opérations d'entrée/sortie.</p>
          </div>

          <div class="hardware-item">
            <i class="mdi mdi-printer"></i>
            <h4>Imprimante Industrielle</h4>
            <p>Configuration et intégration d'une imprimante thermique industrielle pour générer en temps
            réel des étiquettes de traçabilité et des documents d'accompagnement. Le système imprime
            automatiquement les informations essentielles (référence, affaire, date) lors de chaque opération.</p>
          </div>

          <div class="hardware-item">
            <i class="mdi mdi-raspberry-pi"></i>
            <h4>Raspberry Pi</h4>
            <p>Déploiement de Raspberry Pi comme terminaux légers en divers points de l'atelier, offrant
            un accès rapide à l'application sans investissement lourd. Ces mini-ordinateurs se sont révélés
            parfaitement adaptés à un usage industriel : peu coûteux, compacts et suffisamment performants
            pour faire tourner l'interface web.</p>
          </div>

          <div class="hardware-item">
            <i class="mdi mdi-monitor-screenshot"></i>
            <h4>Écrans Tactiles</h4>
            <p>Installation d'écrans tactiles industriels résistants aux environnements difficiles (poussière,
            projections). Ces écrans permettent aux opérateurs d'interagir intuitivement avec le système sans
            avoir besoin de clavier ou souris, facilitant l'adoption et accélérant les opérations quotidiennes.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Learning Card -->
    <section class="card learning-card">
      <div class="card-header">
        <i class="mdi mdi-school"></i>
        <h2>Compétences Développées</h2>
      </div>
      <div class="card-content">
        <div class="learning-grid">
          <div class="learning-category">
            <h4>Développement Full-Stack</h4>
            <ul>
              <li>Frontend moderne avec Vue.js et Vuetify</li>
              <li>API REST avec Symfony et API Platform</li>
              <li>Conception de bases de données</li>
              <li>Architecture micro-services</li>
            </ul>
          </div>

          <div class="learning-category">
            <h4>Environnement Industriel</h4>
            <ul>
              <li>Intégration matériel (scannettes, imprimantes)</li>
              <li>Interfaces tactiles pour opérateurs</li>
              <li>Raspberry Pi en production</li>
              <li>Contraintes de l'environnement atelier</li>
            </ul>
          </div>

          <div class="learning-category">
            <h4>Gestion de Projet</h4>
            <ul>
              <li>Cycle complet : cahier des charges → déploiement</li>
              <li>Communication avec les utilisateurs</li>
              <li>Formation des opérateurs</li>
              <li>Maintenance et support</li>
            </ul>
          </div>

        </div>

        <div class="highlight-box first-experience">
          <i class="mdi mdi-briefcase-check"></i>
          <p>
            <strong>Première expérience professionnelle particulièrement formatrice.</strong>
            J'y ai énormément appris, non seulement sur le plan technique avec la découverte de l'écosystème
            Symfony et Vue.js en contexte réel, mais aussi sur le fonctionnement d'un environnement industriel
            avec ses contraintes spécifiques. Cette mission m'a permis de gagner en <strong>autonomie</strong>
            dans la prise de décisions techniques, en <strong>rigueur</strong> dans le développement et les
            tests, et en <strong>confiance</strong> dans mes capacités à mener à bien un projet complet de
            A à Z. L'interaction quotidienne avec les utilisateurs finaux m'a aussi enseigné l'importance
            de bien comprendre les besoins métier avant de commencer à coder, et la satisfaction de voir son
            travail utilisé concrètement au quotidien par une équipe entière.
          </p>
        </div>

        <div class="impact-stats">
          <div class="stat-box">
            <div class="stat-icon">
              <i class="mdi mdi-check-circle"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">100%</div>
              <div class="stat-label">Traçabilité des stocks</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import {Vue, Component} from 'vue-facing-decorator';

@Component({})
export default class StageComponent extends Vue {
}
</script>

<style scoped>
.project-container {
  font-family: 'Inter', 'Segoe UI', sans-serif;
  line-height: 1.8;
  color: var(--text-color);
  background-color: var(--background-color);
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  padding-left: 100px;
}

/* Hero Section */
.hero-section {
  margin-bottom: 60px;
  padding: 60px 40px;
  background: linear-gradient(135deg, var(--card-background) 0%, var(--dark-bg-secondary) 100%);
  border-radius: 24px;
  border: 1px solid var(--dark-border);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.hero-content {
  display: flex;
  flex-direction: column;
}

.hero-badge {
  display: inline-block;
  width: fit-content;
  padding: 8px 16px;
  background: rgba(87, 112, 190, 0.2);
  color: #5770be;
  border-radius: 20px;
  font-size: 0.85em;
  font-weight: 600;
  margin-bottom: 16px;
  border: 1px solid #5770be;
}

.hero-title {
  font-size: 4.5em;
  font-weight: 800;
  color: var(--h1-color);
  margin: 0 0 16px 0;
  background: linear-gradient(135deg, #ffffff 0%, var(--tertiary-color) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-subtitle {
  font-size: 1.6em;
  color: var(--sub-color);
  margin-bottom: 24px;
  font-weight: 400;
}

.hero-meta {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-muted);
  font-size: 0.95em;
}

.meta-item i {
  color: var(--primary-color);
}

/* Card Styles */
.card {
  background: var(--card-background);
  border-radius: 20px;
  padding: 40px;
  margin-bottom: 32px;
  border: 1px solid var(--dark-border);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 32px rgba(19, 124, 139, 0.2);
  border-color: var(--primary-color);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--dark-border);
}

.card-header i {
  font-size: 32px;
  color: var(--primary-color);
}

.card-header h2 {
  font-size: 2em;
  font-weight: 700;
  color: var(--h2-color);
  margin: 0;
}

.card-content {
  color: var(--text-color);
}

.lead-text {
  font-size: 1.1em;
  line-height: 1.8;
  margin-bottom: 24px;
}

.section-subtitle {
  font-size: 1.4em;
  color: var(--h2-color);
  margin: 0 0 16px 0;
  font-weight: 600;
}

/* Tech Stack */
.tech-stack {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 24px;
}

.tech-badge {
  padding: 10px 20px;
  background: rgba(19, 124, 139, 0.15);
  color: var(--primary-color);
  border-radius: 12px;
  font-size: 0.9em;
  font-weight: 600;
  border: 1px solid rgba(19, 124, 139, 0.3);
  transition: all 0.3s ease;
}

.tech-badge:hover {
  background: rgba(19, 124, 139, 0.25);
  transform: translateY(-2px);
}

/* Company Grid */
.company-grid {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 24px;
}

.company-item {
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 20px;
  padding: 24px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
  transition: all 0.3s ease;
}

.company-item:hover {
  background: var(--dark-bg-card);
  border-color: var(--primary-color);
}

.company-icon {
  width: 80px;
  height: 80px;
  background: rgba(19, 124, 139, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.company-icon i {
  font-size: 40px;
  color: var(--primary-color);
}

.company-content h4 {
  font-size: 1.3em;
  color: var(--h2-color);
  margin: 0 0 8px 0;
  font-weight: 600;
}

.company-content p {
  color: var(--text-muted);
  margin: 0;
}

/* Feature Grid */
.feature-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin: 32px 0;
}

.feature-item {
  padding: 24px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
  text-align: center;
  transition: all 0.3s ease;
}

.feature-item:hover {
  background: var(--dark-bg-card);
  border-color: var(--primary-color);
  transform: translateY(-4px);
}

.feature-icon {
  width: 60px;
  height: 60px;
  background: rgba(19, 124, 139, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.feature-icon i {
  font-size: 28px;
  color: var(--primary-color);
}

.feature-item h4 {
  font-size: 1.1em;
  color: var(--h2-color);
  margin: 0 0 8px 0;
  font-weight: 600;
}

.feature-item p {
  color: var(--text-muted);
  margin: 0;
  font-size: 0.95em;
}

/* Features List */
.features-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
}

.feature-row {
  display: grid;
  grid-template-columns: 60px 1fr;
  gap: 20px;
  padding: 20px;
  background: var(--dark-bg-secondary);
  border-radius: 12px;
  border-left: 3px solid var(--primary-color);
  transition: all 0.3s ease;
}

.feature-row:hover {
  background: var(--dark-bg-card);
  transform: translateX(8px);
}

.feature-row-icon {
  width: 60px;
  height: 60px;
  background: rgba(19, 124, 139, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.feature-row-icon i {
  font-size: 28px;
  color: var(--primary-color);
}

.feature-row-content h4 {
  font-size: 1.2em;
  color: var(--h2-color);
  margin: 0 0 6px 0;
  font-weight: 600;
}

.feature-row-content p {
  color: var(--text-muted);
  margin: 0;
  font-size: 0.95em;
}

/* Hardware Grid */
.hardware-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 24px;
}

.hardware-item {
  padding: 28px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
  text-align: center;
  transition: all 0.3s ease;
}

.hardware-item:hover {
  background: var(--dark-bg-card);
  border-color: var(--primary-color);
  transform: translateY(-4px);
}

.hardware-item i {
  font-size: 48px;
  color: var(--primary-color);
  margin-bottom: 16px;
}

.hardware-item h4 {
  font-size: 1.2em;
  color: var(--h2-color);
  margin: 0 0 8px 0;
  font-weight: 600;
}

.hardware-item p {
  color: var(--text-muted);
  margin: 0;
  font-size: 0.95em;
}

/* Learning Grid */
.learning-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
}

.learning-category {
  padding: 24px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
}

.learning-category h4 {
  font-size: 1.2em;
  color: var(--primary-color);
  margin: 0 0 16px 0;
  font-weight: 600;
}

.learning-category ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.learning-category li {
  padding: 8px 0 8px 24px;
  color: var(--text-color);
  position: relative;
  font-size: 0.95em;
}

.learning-category li::before {
  content: '→';
  position: absolute;
  left: 0;
  color: var(--primary-color);
  font-weight: bold;
}

/* Highlight Box */
.highlight-box {
  padding: 32px;
  background: linear-gradient(135deg, rgba(19, 124, 139, 0.1) 0%, rgba(19, 124, 139, 0.05) 100%);
  border-radius: 16px;
  border: 2px solid var(--primary-color);
  display: flex;
  gap: 20px;
  align-items: center;
  margin-bottom: 32px;
}

.highlight-box i {
  font-size: 48px;
  color: var(--primary-color);
  flex-shrink: 0;
}

.highlight-box p {
  margin: 0;
  font-size: 1.1em;
  line-height: 1.8;
}

/* Impact Stats */
.impact-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
}

.stat-box {
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 20px;
  padding: 28px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
  transition: all 0.3s ease;
}

.stat-box:hover {
  background: var(--dark-bg-card);
  border-color: var(--primary-color);
  transform: translateY(-4px);
}

.stat-icon {
  width: 80px;
  height: 80px;
  background: rgba(19, 124, 139, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon i {
  font-size: 40px;
  color: var(--primary-color);
}

.stat-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.stat-number {
  font-size: 2.5em;
  font-weight: 800;
  color: var(--primary-color);
  line-height: 1;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 0.9em;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* Responsive */
@media (max-width: 768px) {
  .project-container {
    padding-left: 20px;
  }

  .hero-title {
    font-size: 2.8em;
  }

  .company-item, .feature-row, .stat-box {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .company-icon, .feature-row-icon, .stat-icon {
    margin: 0 auto;
  }
}
</style>
